import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MetabaseEmbedComponent } from '../../../components/metabase-embed/metabase-embed.component';
import { RoutingTabComponent } from '../../../components/ui/tabs/routing-tab/routing-tab.component';
import { UserProfileService } from '../../../services/user/user-profile.service';
import {
  DEFAULT_REPORT_PERIOD,
  ReportPeriodDays,
  periodPhrase,
} from '../../../services/reports/report-period';
import { environment } from '../../../../environments/environment';
import { ReportPeriodComponent } from '../messaging-reports/report-period/report-period.component';
import { GatewayLogComponent } from './gateway-log/gateway-log.component';

export type ApiTrafficTab = 'log' | 'dashboard';

const PAGE_LINK = '/organization/smsc-reports';

/**
 * API traffic (/organization/smsc-reports): messages the organisation's
 * systems sent through the SMS API. The gateway's own log, searchable and
 * traceable, and the Metabase dashboard for the same traffic. Gated by role
 * in the route (apiTrafficGuard) as well as in the sidebar.
 */
@Component({
  selector: 'app-main-report',
  standalone: true,
  imports: [
    MatTooltipModule,
    MetabaseEmbedComponent,
    RoutingTabComponent,
    ReportPeriodComponent,
    GatewayLogComponent,
  ],
  templateUrl: './main-report.component.html',
  styleUrl: './main-report.component.css',
})
export class MainReportComponent implements OnInit {
  private userService = inject(UserProfileService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  readonly orgId = this.userService.jwtOrgId;
  /** The SMS dashboard on the default Metabase instance. */
  readonly dashboardId = environment.metabase.smsDashboardId;

  @ViewChild(MetabaseEmbedComponent) embed?: MetabaseEmbedComponent;

  /** Full screen needs the browser's permission and a live dashboard. */
  readonly canFullscreen =
    typeof document !== 'undefined' &&
    !!document.fullscreenEnabled &&
    !(environment as { devMockApi?: boolean }).devMockApi;

  readonly tabs = [
    { title: 'Gateway log', link: PAGE_LINK, queryParams: {} as Record<string, string> },
    { title: 'Dashboard', link: PAGE_LINK, queryParams: { tab: 'dashboard' } },
  ];

  tab: ApiTrafficTab = 'log';
  days: ReportPeriodDays = DEFAULT_REPORT_PERIOD;
  reloadKey = 0;

  get description(): string {
    return this.tab === 'dashboard'
      ? 'Charts for the messages your systems sent through the SMS API.'
      : `Messages your systems sent through the SMS API in ${periodPhrase(this.days)}, as the gateway logged them.`;
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.tab = params.get('tab') === 'dashboard' ? 'dashboard' : 'log';
    });
  }

  setDays(days: ReportPeriodDays): void {
    this.days = days;
  }

  refresh(): void {
    if (this.tab === 'dashboard') this.embed?.refresh();
    else this.reloadKey += 1;
  }

  reloadDashboard(): void {
    this.embed?.refresh();
  }

  openFullscreen(): void {
    this.embed?.enterFullscreen();
  }
}
